import { ref } from 'vue'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/services/firebase'

// 기본 상품 목록
const DEFAULT_PRODUCTS = [
  // 공고 업그레이드
  {
    id: 'premium',
    category: 'job',
    name: '프리미엄',
    description: '메인 배너 노출 + 검색 상단 고정',
    price: 30000,
    duration: 7,
    icon: '👑',
    color: '#3182f6',
    field: 'isPremium',
    order: 1,
    isActive: true
  },
  {
    id: 'pick',
    category: 'job',
    name: '분쟁 PICK',
    description: 'PICK 섹션 노출 + PICK 뱃지',
    price: 20000,
    duration: 7,
    icon: '⭐',
    color: '#a855f7',
    field: 'isPick',
    order: 2,
    isActive: true
  },
  {
    id: 'urgent',
    category: 'job',
    name: '급구',
    description: '급구 섹션 노출 + 급구 뱃지',
    price: 10000,
    duration: 7,
    icon: '🔥',
    color: '#ef4444',
    field: 'isUrgent',
    order: 3,
    isActive: true
  },
  {
    id: 'featured',
    category: 'job',
    name: '추천',
    description: '추천 뱃지 표시',
    price: 5000,
    duration: 7,
    icon: '💎',
    color: '#12b886',
    field: 'isFeatured',
    order: 4,
    isActive: true
  },
  {
    id: 'region_ad',
    category: 'job',
    name: '지역 광고',
    description: '지역별 페이지 상단에 노출됩니다',
    price: 10000,
    duration: 7,
    icon: '📍',
    color: '#3182f6',
    field: 'isRegionAd',
    order: 5,
    isActive: true
  },
  // 배너 광고
  {
    id: 'mainBanner',
    category: 'banner',
    name: '메인 배너',
    description: '홈페이지 상단 슬라이더 노출',
    price: 50000,
    duration: 7,
    icon: '🖼️',
    color: '#f59e0b',
    collection: 'banners',
    order: 6,
    isActive: true
  },
  {
    id: 'topBanner',
    category: 'banner',
    name: '탑배너',
    description: '헤더 아래 띠배너 노출',
    price: 30000,
    duration: 7,
    icon: '📢',
    color: '#3182f6',
    collection: 'topBanners',
    order: 7,
    isActive: true
  },
  {
    id: 'subBanner',
    category: 'banner',
    name: '서브배너',
    description: '홈페이지 중간 띠배너 노출',
    price: 20000,
    duration: 7,
    icon: '📣',
    color: '#ff6b35',
    collection: 'subBanners',
    order: 8,
    isActive: true
  },
  {
    id: 'sidebarAd',
    category: 'banner',
    name: '사이드바 광고',
    description: '마이페이지 사이드바 배너',
    price: 15000,
    duration: 7,
    icon: '📱',
    color: '#7950f2',
    collection: 'sidebarAds',
    order: 9,
    isActive: true
  }
]

export function useProducts() {
  const products = ref([])
  const loading = ref(false)
  const error = ref(null)

  // 상품 목록 로드
  const loadProducts = async () => {
    loading.value = true
    error.value = null

    try {
      const productsRef = doc(db, 'settings', 'products')
      const snapshot = await getDoc(productsRef)

      if (snapshot.exists()) {
        const data = snapshot.data()
        products.value = data.items || DEFAULT_PRODUCTS
      } else {
        // 기본값 저장
        await setDoc(productsRef, {
          items: DEFAULT_PRODUCTS,
          updatedAt: serverTimestamp()
        })
        products.value = DEFAULT_PRODUCTS
      }
    } catch (e) {
      console.error('상품 로드 실패:', e)
      error.value = e.message
      products.value = DEFAULT_PRODUCTS
    } finally {
      loading.value = false
    }
  }

  // 상품 저장
  const saveProducts = async (items) => {
    loading.value = true
    error.value = null

    try {
      const productsRef = doc(db, 'settings', 'products')
      await setDoc(productsRef, {
        items,
        updatedAt: serverTimestamp()
      })
      products.value = items
      return true
    } catch (e) {
      console.error('상품 저장 실패:', e)
      error.value = e.message
      return false
    } finally {
      loading.value = false
    }
  }

  // 특정 상품 가져오기
  const getProduct = (productId) => {
    return products.value.find(p => p.id === productId)
  }

  // 카테고리별 상품 가져오기
  const getProductsByCategory = (category) => {
    return products.value
      .filter(p => p.category === category && p.isActive)
      .sort((a, b) => a.order - b.order)
  }

  // 공고 업그레이드 상품
  const jobProducts = () => getProductsByCategory('job')

  // 배너 광고 상품
  const bannerProducts = () => getProductsByCategory('banner')

  // 총 가격 계산
  const calculateTotal = (selectedProductIds) => {
    return selectedProductIds.reduce((total, id) => {
      const product = getProduct(id)
      return total + (product?.price || 0)
    }, 0)
  }

  // 포인트 포맷
  const formatPrice = (price) => {
    return new Intl.NumberFormat('ko-KR').format(price)
  }

  // 만료일 계산
  const calculateExpiry = (duration) => {
    const expiry = new Date()
    expiry.setDate(expiry.getDate() + duration)
    return expiry
  }

  return {
    products,
    loading,
    error,
    DEFAULT_PRODUCTS,
    loadProducts,
    saveProducts,
    getProduct,
    getProductsByCategory,
    jobProducts,
    bannerProducts,
    calculateTotal,
    formatPrice,
    calculateExpiry
  }
}